import React, { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ChevronRight,
  File,
  Folder,
  Loader2,
  Paperclip,
} from "lucide-react";
import { SiGoogledrive } from "react-icons/si";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { GoogleConnectCard } from "@/components/google/GoogleConnectCard";
import {
  useGoogleConnection,
  isGoogleAuthError,
} from "@/contexts/GoogleConnectionContext";
import { getStoredToken } from "@/lib/googleAuth";
import {
  DriveAuthError,
  getDriveFileBlob,
  listAttachableFolderChildren,
  type DriveEntry,
} from "@/services/googleDriveService";
import type { PersistenceContext, PersistedFileResult } from "@/components/GoogleDrivePicker";
import { documentPersistenceService } from "@/services/documentPersistenceService";

type Breadcrumb = { id: string; name: string };

type GoogleDriveAttachPickerProps = {
  onFilesAttached: (
    files: Array<{ filename: string; blob: Blob; mimeType: string }>
  ) => void | Promise<void>;
  persistToDatabase?: boolean;
  persistenceContext?: PersistenceContext;
  onFilesPersisted?: (files: PersistedFileResult[]) => void;
};

export function GoogleDriveAttachPicker({
  onFilesAttached,
  persistToDatabase = false,
  persistenceContext,
  onFilesPersisted,
}: GoogleDriveAttachPickerProps) {
  const {
    connection,
    connectDrive,
    disconnect,
    applySession,
    handleAuthFailure,
  } = useGoogleConnection();

  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>([
    { id: "root", name: "My Drive" },
  ]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isAttaching, setIsAttaching] = useState(false);
  const [attachError, setAttachError] = useState<string | null>(null);

  const currentFolderId = breadcrumbs[breadcrumbs.length - 1]?.id ?? "root";

  const folderQuery = useQuery({
    queryKey: ["google-drive", "attach", currentFolderId, connection.token],
    queryFn: async () => {
      try {
        const token = connection.token ?? getStoredToken();
        if (!token) throw new DriveAuthError();
        const result = await listAttachableFolderChildren(currentFolderId, token);
        const latestToken = getStoredToken();
        if (latestToken && latestToken !== connection.token) {
          applySession(latestToken);
        }
        return result;
      } catch (error) {
        if (isGoogleAuthError(error)) {
          handleAuthFailure();
        }
        throw error;
      }
    },
    enabled: connection.driveConnected && !!connection.token,
    staleTime: 60_000,
    retry: (failureCount, error) => {
      if (isGoogleAuthError(error)) return false;
      return failureCount < 1;
    },
  });

  const entries = useMemo(() => {
    const folders = folderQuery.data?.folders ?? [];
    const files = folderQuery.data?.files ?? [];
    return [
      ...folders.map((entry) => ({ ...entry, kind: "folder" as const })),
      ...files.map((entry) => ({ ...entry, kind: "file" as const })),
    ];
  }, [folderQuery.data]);

  const selectedFiles = useMemo(() => {
    const files = folderQuery.data?.files ?? [];
    return files.filter((file) => selectedIds.has(file.id));
  }, [folderQuery.data?.files, selectedIds]);

  const navigateToFolder = useCallback((folder: DriveEntry) => {
    setBreadcrumbs((prev) => [...prev, { id: folder.id, name: folder.name }]);
    setSelectedIds(new Set());
  }, []);

  const navigateToBreadcrumb = useCallback((index: number) => {
    setBreadcrumbs((prev) => prev.slice(0, index + 1));
    setSelectedIds(new Set());
  }, []);

  const toggleFile = useCallback((fileId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(fileId)) next.delete(fileId);
      else next.add(fileId);
      return next;
    });
  }, []);

  const handleAttach = useCallback(async () => {
    if (selectedFiles.length === 0) return;

    const token = connection.token ?? getStoredToken();
    if (!token) {
      setAttachError("Google Drive session expired. Please sign in again.");
      return;
    }

    setIsAttaching(true);
    setAttachError(null);

    try {
      const attached: Array<{ filename: string; blob: Blob; mimeType: string }> = [];
      const persisted: PersistedFileResult[] = [];

      for (const file of selectedFiles) {
        const blob = await getDriveFileBlob(file, token, false);
        const filename = file.name;
        const mimeType = blob.type || file.mimeType || "application/octet-stream";
        attached.push({ filename, blob, mimeType });

        if (persistToDatabase && persistenceContext) {
          const saved = await documentPersistenceService.uploadAndPersist(
            blob,
            filename,
            mimeType,
            {
              threadId: persistenceContext.threadId,
              spaceId: persistenceContext.spaceId,
              topicId: persistenceContext.topicId,
              folderId: persistenceContext.folderId,
              workspaceId: persistenceContext.workspaceId,
            },
            {
              googleDriveFileId: file.id,
            }
          );
          persisted.push({
            sourceId: saved.sourceId,
            storageId: saved.storageId,
            filename,
            url: saved.url || "",
          });
        }
      }

      await onFilesAttached(attached);
      if (persisted.length > 0) {
        onFilesPersisted?.(persisted);
      }
      setSelectedIds(new Set());
    } catch (error) {
      console.error("Failed to attach Drive files:", error);
      if (isGoogleAuthError(error)) {
        handleAuthFailure();
        setAttachError("Google Drive session expired. Please sign in again.");
      } else {
        setAttachError(
          error instanceof Error ? error.message : "Failed to attach selected files."
        );
      }
    } finally {
      setIsAttaching(false);
    }
  }, [
    selectedFiles,
    connection.token,
    persistToDatabase,
    persistenceContext,
    onFilesAttached,
    onFilesPersisted,
    handleAuthFailure,
  ]);

  if (!connection.driveConnected) {
    return (
      <GoogleConnectCard
        connection={connection}
        onConnect={connectDrive}
        onDisconnect={disconnect}
        service="drive"
        description="Sign in to browse your Drive and attach files to this chat."
        icon={<SiGoogledrive className="h-5 w-5 text-[#4285F4]" />}
      />
    );
  }

  return (
    <div className="space-y-3">
      <GoogleConnectCard
        connection={connection}
        onConnect={connectDrive}
        onDisconnect={disconnect}
        service="drive"
        icon={<SiGoogledrive className="h-5 w-5 text-[#4285F4]" />}
      />

      <div className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
        {breadcrumbs.map((crumb, index) => (
          <span key={crumb.id} className="flex items-center gap-1">
            {index > 0 && <ChevronRight className="h-3 w-3" />}
            <button
              type="button"
              className="hover:text-foreground transition-colors"
              onClick={() => navigateToBreadcrumb(index)}
            >
              {crumb.name}
            </button>
          </span>
        ))}
      </div>

      <ScrollArea className="h-[280px] rounded-md border">
        <div className="p-1">
          {folderQuery.isLoading ? (
            <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading files…
            </div>
          ) : folderQuery.isError ? (
            <div className="px-3 py-6 text-sm text-destructive">
              Failed to load Drive folder. Try signing in again.
            </div>
          ) : entries.length === 0 ? (
            <div className="px-3 py-6 text-sm text-muted-foreground text-center">
              No attachable files in this folder.
            </div>
          ) : (
            entries.map((entry) =>
              entry.kind === "folder" ? (
                <button
                  key={entry.id}
                  type="button"
                  className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm hover:bg-muted/60 transition-colors"
                  onClick={() => navigateToFolder(entry)}
                >
                  <Folder className="h-4 w-4 shrink-0 text-amber-500" />
                  <span className="truncate text-left">{entry.name}</span>
                  <ChevronRight className="ml-auto h-4 w-4 text-muted-foreground" />
                </button>
              ) : (
                <div
                  key={entry.id}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm hover:bg-muted/60 transition-colors"
                >
                  <Checkbox
                    checked={selectedIds.has(entry.id)}
                    onCheckedChange={() => toggleFile(entry.id)}
                    aria-label={`Select ${entry.name}`}
                  />
                  <File className="h-4 w-4 shrink-0 text-blue-500" />
                  <span className="truncate">{entry.name}</span>
                </div>
              )
            )
          )}
        </div>
      </ScrollArea>

      {attachError && (
        <p className="text-sm text-destructive">{attachError}</p>
      )}

      <Button
        className="w-full"
        disabled={selectedFiles.length === 0 || isAttaching}
        onClick={handleAttach}
      >
        {isAttaching ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Attaching…
          </>
        ) : (
          <>
            <Paperclip className="mr-2 h-4 w-4" />
            Attach {selectedFiles.length > 0 ? `${selectedFiles.length} ` : ""}
            file{selectedFiles.length === 1 ? "" : "s"}
          </>
        )}
      </Button>
    </div>
  );
}
